import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import TopBar from '../components/TopBar'
import athletesService from '../services/athletesService'

const AthleteDetail = () => {
  const { id } = useParams()
  const [athleteData, setAthleteData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchAthleteDetails()
  }, [id])

  const fetchAthleteDetails = async () => {
    try {
      setLoading(true)
      const data = await athletesService.getAthleteById(id)
      setAthleteData(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-off">
        <TopBar />
        <div className="main" style={{ maxWidth: '880px', margin: '0 auto', padding: '40px 20px 80px' }}>
          <div className="text-center" style={{ color: '#F06E90', fontFamily: 'var(--font-d)' }}>
            Loading athlete details...
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-off">
        <TopBar />
        <div className="main" style={{ maxWidth: '880px', margin: '0 auto', padding: '40px 20px 80px' }}>
          <div className="text-center" style={{ color: '#ef4444', fontFamily: 'var(--font-d)' }}>
            Error: {error}
          </div>
        </div>
      </div>
    )
  }

  if (!athleteData) {
    return (
      <div className="min-h-screen bg-off">
        <TopBar />
        <div className="main" style={{ maxWidth: '880px', margin: '0 auto', padding: '40px 20px 80px' }}>
          <div className="text-center" style={{ color: '#F06E90', fontFamily: 'var(--font-d)' }}>
            Athlete not found.
          </div>
        </div>
      </div>
    )
  }

  const { athlete, media, socialMedia, achievements } = athleteData

  // Filter for main photo and video - use first image/video if specific descriptions not found
  const images = media.filter(m => m.file_type === 'image')
  const videos = media.filter(m => m.file_type === 'video')
  const mainPhoto = images.find(m => m.description?.includes('NIL Official Photo')) || images[0]
  const mainVideo = videos.find(m => m.description?.includes('NIL Head Movement Video')) || videos[0]
  const otherImages = images.filter(m => m.id !== mainPhoto?.id)
  const otherVideos = videos.filter(m => m.id !== mainVideo?.id)

  // Helper function to get social media icon, proper URL, and username
  const getSocialMediaInfo = (platform, url) => {
    const platformLower = platform.toLowerCase()
    let icon = ''
    let directUrl = url
    let username = url

    // Map platforms to icons and ensure proper URLs
    switch (platformLower) {
      case 'twitter':
      case 'x':
        icon = '𝕏' // X icon
        // Extract username and ensure proper URL
        if (url.includes('twitter.com') || url.includes('x.com')) {
          username = url.split('/').pop().replace('@', '') || url
        } else {
          username = url.replace('@', '')
          directUrl = `https://x.com/${username}`
        }
        break
      case 'instagram':
        icon = '📷' // Instagram icon
        // Extract username and ensure proper URL
        if (url.includes('instagram.com')) {
          username = url.split('/').pop().replace('@', '') || url
        } else {
          username = url.replace('@', '')
          directUrl = `https://instagram.com/${username}`
        }
        break
      case 'facebook':
        icon = 'f' // Facebook icon
        // Extract username and ensure proper URL
        if (url.includes('facebook.com')) {
          username = url.split('/').pop().replace('@', '') || url
        } else {
          username = url.replace('@', '')
          directUrl = `https://facebook.com/${username}`
        }
        break
      case 'linkedin':
        icon = 'in' // LinkedIn icon
        // Extract username and ensure proper URL
        if (url.includes('linkedin.com')) {
          username = url.split('/').pop().replace('@', '') || url
        } else {
          username = url.replace('@', '')
          directUrl = `https://linkedin.com/in/${username}`
        }
        break
      case 'tiktok':
        icon = '♬' // TikTok icon
        // Extract username and ensure proper URL
        if (url.includes('tiktok.com')) {
          username = url.split('/').pop().replace('@', '') || url
        } else {
          username = url.replace('@', '')
          directUrl = `https://tiktok.com/@${username}`
        }
        break
      case 'youtube':
        icon = '▶' // YouTube icon
        // Extract username and ensure proper URL
        if (url.includes('youtube.com')) {
          username = url.split('/').pop().replace('@', '') || url
        } else {
          username = url.replace('@', '')
          directUrl = `https://youtube.com/${username}`
        }
        break
      default:
        icon = '🔗' // Generic link icon
        username = url
    }

    return { icon, username, directUrl }
  }

  
  return (
    <div className="min-h-screen bg-off" style={{ background: '#111115' }}>
      <TopBar />
      
      <div className="main" style={{ maxWidth: '880px', margin: '0 auto', padding: '40px 20px 80px' }}>
        <Link 
          to="/athletes" 
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            color: '#F06E90',
            textDecoration: 'none',
            fontFamily: 'var(--font-d)',
            fontSize: '14px',
            marginBottom: '24px',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.opacity = '0.8'
          }}
          onMouseOut={(e) => {
            e.target.style.opacity = '1'
          }}
        >
          ← Back to Athletes List
        </Link>

        <div style={{
          background: 'rgba(232,80,122,0.08)',
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.07)',
          overflow: 'hidden',
          backdropFilter: 'blur(10px)'
        }}>
          {/* Header Section */}
          <div style={{
            padding: '32px',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            background: 'rgba(232,80,122,0.08)'
          }}>
            <h1 style={{
              fontFamily: 'var(--font-d)',
              fontSize: '32px',
              color: '#FFFFFF',
              fontWeight: '500',
              marginBottom: '8px',
              letterSpacing: '0.02em'
            }}>
              {athlete.display_name || `${athlete.first_name} ${athlete.middle_name} ${athlete.last_name}`}
            </h1>
            {athlete.display_name && (
              <p style={{
                fontFamily: 'var(--font-b)',
                fontSize: '16px',
                color: 'rgba(255,255,255,0.7)',
                fontStyle: 'italic',
                marginBottom: '24px'
              }}>
                Legal name: {athlete.first_name} {athlete.middle_name} {athlete.last_name}
              </p>
            )}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <div>
                <span style={{
                  fontFamily: 'var(--font-d)',
                  fontSize: '12px',
                  color: '#FFFFFF',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase'
                }}>
                  School:
                </span>
                <div style={{
                  fontFamily: 'var(--font-d)',
                  fontSize: '16px',
                  color: '#FFFFFF',
                  marginTop: '4px'
                }}>
                  {athlete.schools?.name || 'Unknown'}
                </div>
              </div>
              <div>
                <span style={{
                  fontFamily: 'var(--font-d)',
                  fontSize: '12px',
                  color: '#FFFFFF',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase'
                }}>
                  Sport:
                </span>
                <div style={{
                  fontFamily: 'var(--font-d)',
                  fontSize: '16px',
                  color: '#FFFFFF',
                  marginTop: '4px'
                }}>
                  {athlete.sports?.name || 'Unknown'}
                </div>
              </div>
              <div>
                <span style={{
                  fontFamily: 'var(--font-d)',
                  fontSize: '12px',
                  color: '#FFFFFF',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase'
                }}>
                  Position:
                </span>
                <div style={{
                  fontFamily: 'var(--font-d)',
                  fontSize: '16px',
                  color: '#FFFFFF',
                  marginTop: '4px'
                }}>
                  {athlete.position || 'Not specified'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ padding: '32px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }}>
            {/* Commercial Bio */}
            {athlete.commercial_bio && (
              <div>
                <h2 style={{
                  fontFamily: 'var(--font-d)',
                  fontSize: '24px',
                  color: '#F06E90',
                  fontWeight: '500',
                  marginBottom: '24px',
                  letterSpacing: '0.02em'
                }}>
                  Commercial Bio
                </h2>
                <div style={{
                  background: 'rgba(232,80,122,0.08)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.07)'
                }}>
                  <div style={{
                    fontFamily: 'var(--font-b)',
                    fontSize: '16px',
                    color: '#FFFFFF',
                    lineHeight: '1.6'
                  }}>
                    {athlete.commercial_bio}
                  </div>
                </div>
              </div>
            )}

            {/* Media Section */}
            <div>
              <h2 style={{
                fontFamily: 'var(--font-d)',
                fontSize: '24px',
                color: '#F06E90',
                fontWeight: '500',
                marginBottom: '24px',
                letterSpacing: '0.02em'
              }}>
                Media
              </h2>
              
              {/* Main Photo */}
              {mainPhoto && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{
                    fontFamily: 'var(--font-d)',
                    fontSize: '16px',
                    color: '#FFFFFF',
                    marginBottom: '12px',
                    letterSpacing: '0.04em'
                  }}>
                    Official Photo
                  </h3>
                  <img 
                    src={mainPhoto.publicUrl} 
                    alt={`${athlete.first_name} ${athlete.last_name}`}
                    style={{
                      width: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      maxHeight: '400px',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      console.error('Image failed to load:', e.target.src)
                      e.target.style.display = 'none'
                    }}
                    onLoad={(e) => {
                      console.log('Image loaded successfully:', e.target.src)
                    }}
                  />
                </div>
              )}

              {/* Main Video */}
              {mainVideo && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{
                    fontFamily: 'var(--font-d)',
                    fontSize: '16px',
                    color: '#FFFFFF',
                    marginBottom: '12px',
                    letterSpacing: '0.04em'
                  }}>
                    Official Video
                  </h3>
                  <video 
                    controls
                    style={{
                      width: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      maxHeight: '400px'
                    }}
                    onError={(e) => {
                      console.error('Video failed to load:', e.target.src)
                    }}
                    onLoadStart={(e) => {
                      console.log('Video loading started:', e.target.src)
                    }}
                    onCanPlay={(e) => {
                      console.log('Video can play:', e.target.src)
                    }}
                  >
                    <source src={mainVideo.publicUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}

              {/* Other Images */}
              {otherImages.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{
                    fontFamily: 'var(--font-d)',
                    fontSize: '16px',
                    color: '#FFFFFF',
                    marginBottom: '12px',
                    letterSpacing: '0.04em'
                  }}>
                    Additional Photos
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                    {otherImages.map(image => (
                      <img 
                        key={image.id}
                        src={image.publicUrl} 
                        alt={image.description}
                        style={{
                          width: '100%',
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Other Videos */}
              {otherVideos.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{
                    fontFamily: 'var(--font-d)',
                    fontSize: '16px',
                    color: '#FFFFFF',
                    marginBottom: '12px',
                    letterSpacing: '0.04em'
                  }}>
                    Additional Videos
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {otherVideos.map(video => (
                      <video 
                        key={video.id}
                        controls
                        style={{
                          width: '100%',
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}
                      >
                        <source src={video.publicUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Athlete Information Section */}
            <div>
              <h2 style={{
                fontFamily: 'var(--font-d)',
                fontSize: '24px',
                color: '#F06E90',
                fontWeight: '500',
                marginBottom: '24px',
                letterSpacing: '0.02em'
              }}>
                Athlete Information
              </h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                
                <div>
                  <h3 style={{
                    fontFamily: 'var(--font-d)',
                    fontSize: '16px',
                    color: '#FFFFFF',
                    marginBottom: '12px',
                    letterSpacing: '0.04em'
                  }}>
                    Academic Information
                  </h3>
                  <div style={{
                    background: 'rgba(232,80,122,0.08)',
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.07)'
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{
                        fontFamily: 'var(--font-d)',
                        fontSize: '12px',
                        color: '#FFFFFF',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase'
                      }}>
                        Graduation Year:
                      </span>
                      <div style={{
                        fontFamily: 'var(--font-b)',
                        fontSize: '14px',
                        color: '#FFFFFF',
                        marginTop: '4px'
                      }}>
                        {athlete.graduation_year}
                      </div>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{
                        fontFamily: 'var(--font-d)',
                        fontSize: '12px',
                        color: '#FFFFFF',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase'
                      }}>
                        Eligibility:
                      </span>
                      <div style={{
                        fontFamily: 'var(--font-b)',
                        fontSize: '14px',
                        color: '#FFFFFF',
                        marginTop: '4px'
                      }}>
                        {athlete.eligibility}
                      </div>
                    </div>
                    <div>
                      <span style={{
                        fontFamily: 'var(--font-d)',
                        fontSize: '12px',
                        color: '#FFFFFF',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase'
                      }}>
                        Jersey Number:
                      </span>
                      <div style={{
                        fontFamily: 'var(--font-b)',
                        fontSize: '14px',
                        color: '#FFFFFF',
                        marginTop: '4px'
                      }}>
                        {athlete.jersey_number || 'Not assigned'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                {socialMedia && socialMedia.length > 0 && (
                  <div>
                    <h3 style={{
                      fontFamily: 'var(--font-d)',
                      fontSize: '16px',
                      color: '#FFFFFF',
                      marginBottom: '12px',
                      letterSpacing: '0.04em'
                    }}>
                      Social Media
                    </h3>
                    <div style={{
                      background: 'rgba(232,80,122,0.08)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.07)'
                    }}>
                      {socialMedia.map((sm) => {
                        const { icon, username, directUrl } = getSocialMediaInfo(sm.platform, sm.url)
                        return (
                          <div key={sm.id} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            marginBottom: '12px',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            background: 'rgba(255,255,255,0.02)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.02)'
                          }}>
                            <span style={{
                              fontSize: '18px',
                              marginRight: '12px',
                              width: '24px',
                              textAlign: 'center',
                              fontFamily: 'var(--font-d)',
                              color: '#F06E90'
                            }}>
                              {icon}
                            </span>
                            <a 
                              href={directUrl} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              style={{
                                color: '#F06E90',
                                textDecoration: 'none',
                                fontFamily: 'var(--font-b)',
                                fontSize: '14px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease',
                                flex: 1
                              }}
                              onMouseOver={(e) => {
                                e.target.style.textDecoration = 'underline'
                                e.target.style.color = '#FFD700'
                              }}
                              onMouseOut={(e) => {
                                e.target.style.textDecoration = 'none'
                                e.target.style.color = 'var(--gold)'
                              }}
                            >
                              {username}
                            </a>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Achievements */}
                {achievements && achievements.length > 0 && (
                  <div>
                    <h3 style={{
                      fontFamily: 'var(--font-d)',
                      fontSize: '16px',
                      color: '#FFFFFF',
                      marginBottom: '12px',
                      letterSpacing: '0.04em'
                    }}>
                      Achievements
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {achievements.map((achievement) => (
                        <div key={achievement.id} style={{
                          background: 'rgba(232,80,122,0.08)',
                          padding: '12px',
                          borderRadius: '12px',
                          border: '1px solid rgba(255,255,255,0.07)'
                        }}>
                          <div style={{
                            fontFamily: 'var(--font-d)',
                            fontSize: '14px',
                            color: '#FFFFFF',
                            fontWeight: '500',
                            marginBottom: '4px'
                          }}>
                            {achievement.title}
                          </div>
                          <div style={{
                            fontFamily: 'var(--font-b)',
                            fontSize: '13px',
                            color: 'rgba(255,255,255,0.8)',
                            marginBottom: '4px'
                          }}>
                            {achievement.description}
                          </div>
                          {achievement.date_achieved && (
                            <div style={{
                              fontFamily: 'var(--font-b)',
                              fontSize: '11px',
                              color: 'rgba(255,255,255,0.6)'
                            }}>
                              {new Date(achievement.date_achieved).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AthleteDetail
